import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, Clock, RefreshCw, Activity, AlertCircle } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import axios from 'axios';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');
  const [resendCount, setResendCount] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);

  const validateEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email);
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');

  if (!email.trim()) {
    setError('Email address is required');
    return;
  }

  if (!validateEmail(email)) {
    setError('Please enter a valid email address');
    return;
  }

  setIsLoading(true);
console.log("hitting")
  try {
    const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/users/forgot-password`, { email });

    // If success
    if (response.data) {
      setEmailSent(true);
      setResendCount(prev => prev + 1);

      // Start resend cooldown (30 seconds)
      setCanResend(false);
      setResendTimer(30);

      const countdown = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(countdown);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  } catch (error) {
    if (error.response?.data?.error) {
      setError(error.response.data.error);
    } else {
      setError('Unable to send recovery email. Please try again.');
    }
  } finally {
    setIsLoading(false);
  }
};

  const handleResend = () => {
    if (canResend) {
      handleSubmit({ preventDefault: () => {} });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo & Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-full mb-4">
            <Activity className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">MedAdmin Dashboard</h1>
          <p className="text-gray-600">Healthcare Account Recovery</p>
        </div>

        {/* Recovery Form */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          {!emailSent ? (
            <>
              {/* Request Form */}
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Forgot Password?</h2>
                <p className="text-gray-600">
                  Enter your email address and we'll send you a secure link to reset your password.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-red-600 text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError('');
                      }}
                      placeholder="Enter your registered email"
                      className={`pl-10 ${error ? 'border-red-300 focus:border-red-500' : 'focus:border-indigo-500'}`}
                      required
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Sending Recovery Email...
                    </div>
                  ) : (
                    <>
                      <Mail className="w-5 h-5 mr-2" />
                      Send Recovery Email
                    </>
                  )}
                </Button>
              </form>

              {/* Security Information */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="text-sm font-medium text-blue-900 mb-2">Security Information</h3>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Recovery links expire after 24 hours</li>
                  <li>• Only registered accounts can request password reset</li>
                  <li>• Check your spam folder if email doesn't arrive</li>
                </ul>
              </div>
            </>
          ) : (
            <>
              {/* Success State */}
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Recovery Email Sent!</h2>
                <p className="text-gray-600 mb-6">
                  We've sent a secure password recovery link to:
                </p>
                <div className="bg-gray-50 rounded-lg p-3 mb-6">
                  <p className="font-medium text-gray-900">{email}</p>
                </div>
              </div>

              {/* Timeline Information */}
              <div className="space-y-4 mb-6">
                <div className="flex items-start">
                  <Clock className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Expected Delivery</p>
                    <p className="text-sm text-gray-600">Usually arrives within 2-5 minutes</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-amber-500 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Can't Find the Email?</p>
                    <p className="text-sm text-gray-600">Check spam/junk folders and verify the email address</p>
                  </div>
                </div>
              </div>

              {/* Resend Option */}
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm text-gray-600 mb-3">Didn't receive the email?</p>
                <Button 
                  onClick={handleResend}
                  disabled={!canResend || isLoading}
                  variant="outline"
                  className="w-full flex items-center justify-center"
                >
                  {!canResend ? (
                    <>
                      <Clock className="w-4 h-4 mr-2" />
                      Resend in {resendTimer}s
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resend Recovery Email
                    </>
                  )}
                </Button>
                
                {resendCount > 0 && (
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Email sent {resendCount} {resendCount === 1 ? 'time' : 'times'}
                  </p>
                )}
              </div>

              {/* Additional Support */}
              <div className="mt-6 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600 text-center">
                  Still having trouble? Contact your healthcare IT administrator
                </p>
              </div>
            </>
          )}

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link 
              to="/" 
              className="inline-flex items-center text-indigo-600 hover:text-indigo-800 font-medium transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-gray-500">
            Secure password recovery for healthcare professionals
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;